import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, Lock, User, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailAuthForm({ onSuccess, onSwitchToGoogle }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const content = {
    es: {
      login: 'Iniciar sesión',
      register: 'Crear cuenta',
      email: 'Email',
      password: 'Contraseña',
      name: 'Nombre',
      emailPlaceholder: 'tu@email.com',
      passwordPlaceholder: 'Mínimo 6 caracteres',
      namePlaceholder: 'Tu nombre',
      loginBtn: 'Entrar',
      registerBtn: 'Registrarse',
      loading: 'Cargando...',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      createAccount: 'Crear una',
      loginHere: 'Inicia sesión',
      orContinueWith: 'o continúa con',
      googleLogin: 'Google',
      errorInvalidEmail: 'Email inválido',
      errorShortPassword: 'La contraseña debe tener al menos 6 caracteres',
      errorEmptyName: 'El nombre es requerido',
      errorGeneric: 'Error al procesar la solicitud',
      successRegister: '¡Cuenta creada exitosamente!',
      successLogin: '¡Bienvenido de nuevo!'
    },
    en: {
      login: 'Sign in',
      register: 'Create account',
      email: 'Email',
      password: 'Password',
      name: 'Name',
      emailPlaceholder: 'your@email.com',
      passwordPlaceholder: 'Minimum 6 characters',
      namePlaceholder: 'Your name',
      loginBtn: 'Sign in',
      registerBtn: 'Sign up',
      loading: 'Loading...',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      createAccount: 'Create one',
      loginHere: 'Sign in',
      orContinueWith: 'or continue with',
      googleLogin: 'Google',
      errorInvalidEmail: 'Invalid email',
      errorShortPassword: 'Password must be at least 6 characters',
      errorEmptyName: 'Name is required',
      errorGeneric: 'Error processing request',
      successRegister: 'Account created successfully!',
      successLogin: 'Welcome back!'
    },
    gl: {
      login: 'Iniciar sesión',
      register: 'Crear conta',
      email: 'Email',
      password: 'Contrasinal',
      name: 'Nome',
      emailPlaceholder: 'teu@email.com',
      passwordPlaceholder: 'Mínimo 6 caracteres',
      namePlaceholder: 'O teu nome',
      loginBtn: 'Entrar',
      registerBtn: 'Rexistrarse',
      loading: 'Cargando...',
      noAccount: 'Non tes conta?',
      hasAccount: 'Xa tes conta?',
      createAccount: 'Crear unha',
      loginHere: 'Inicia sesión',
      orContinueWith: 'ou continúa con',
      googleLogin: 'Google',
      errorInvalidEmail: 'Email inválido',
      errorShortPassword: 'O contrasinal debe ter polo menos 6 caracteres',
      errorEmptyName: 'O nome é requirido',
      errorGeneric: 'Erro ao procesar a solicitude',
      successRegister: 'Conta creada exitosamente!',
      successLogin: 'Benvido de novo!'
    },
    ca: {
      login: 'Iniciar sessió',
      register: 'Crear compte',
      email: 'Email',
      password: 'Contrasenya',
      name: 'Nom',
      emailPlaceholder: 'teu@email.com',
      passwordPlaceholder: 'Mínim 6 caràcters',
      namePlaceholder: 'El teu nom',
      loginBtn: 'Entrar',
      registerBtn: 'Registrar-se',
      loading: 'Carregant...',
      noAccount: 'No tens compte?',
      hasAccount: 'Ja tens compte?',
      createAccount: 'Crear un',
      loginHere: 'Inicia sessió',
      orContinueWith: 'o continua amb',
      googleLogin: 'Google',
      errorInvalidEmail: 'Email invàlid',
      errorShortPassword: 'La contrasenya ha de tenir almenys 6 caràcters',
      errorEmptyName: 'El nom és requerit',
      errorGeneric: 'Error en processar la sol·licitud',
      successRegister: 'Compte creat exitosament!',
      successLogin: 'Benvingut de nou!'
    },
    zh: {
      login: '登录',
      register: '创建账户',
      email: '邮箱',
      password: '密码',
      name: '姓名',
      emailPlaceholder: 'your@email.com',
      passwordPlaceholder: '至少6个字符',
      namePlaceholder: '您的姓名',
      loginBtn: '登录',
      registerBtn: '注册',
      loading: '加载中...',
      noAccount: '没有账户？',
      hasAccount: '已有账户？',
      createAccount: '创建一个',
      loginHere: '登录',
      orContinueWith: '或继续使用',
      googleLogin: 'Google',
      errorInvalidEmail: '邮箱无效',
      errorShortPassword: '密码至少需要6个字符',
      errorEmptyName: '姓名是必需的',
      errorGeneric: '处理请求时出错',
      successRegister: '账户创建成功！',
      successLogin: '欢迎回来！'
    }
  };

  const c = content[lang] || content.es;

  const validateForm = () => {
    if (!email || !email.includes('@')) {
      toast.error(c.errorInvalidEmail);
      return false;
    }
    if (password.length < 6) {
      toast.error(c.errorShortPassword);
      return false;
    }
    if (!isLogin && !name.trim()) {
      toast.error(c.errorEmptyName);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email, password }
        : { email, password, name };

      const response = await axios.post(`${API}${endpoint}`, payload, {
        withCredentials: true
      });

      toast.success(isLogin ? c.successLogin : c.successRegister);
      onSuccess(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      const message = error.response?.data?.detail || c.errorGeneric;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full" data-testid="email-auth-form">
      {/* Tab Switcher */}
      <div className="flex mb-6 bg-zinc-100 dark:bg-zinc-700 rounded-full p-1">
        <button
          type="button"
          onClick={() => setIsLogin(true)}
          className={`flex-1 py-2 px-4 rounded-full text-sm font-bold transition-all ${
            isLogin 
              ? 'bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 shadow-sm text-zinc-900 dark:text-white' 
              : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
          }`}
        >
          {c.login}
        </button>
        <button
          type="button"
          onClick={() => setIsLogin(false)}
          className={`flex-1 py-2 px-4 rounded-full text-sm font-bold transition-all ${
            !isLogin 
              ? 'bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 shadow-sm text-zinc-900 dark:text-white' 
              : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
          }`}
        >
          {c.register}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name field (only for register) */}
        {!isLogin && (
          <div>
            <label className="block text-xs font-bold mb-1.5 text-zinc-900 dark:text-white">{c.name}</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={c.namePlaceholder}
                className="w-full h-12 rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 pl-11 pr-4 text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
                data-testid="name-input"
              />
            </div>
          </div>
        )}

        {/* Email field */}
        <div>
          <label className="block text-xs font-bold mb-1.5 text-zinc-900 dark:text-white">{c.email}</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={c.emailPlaceholder}
              className="w-full h-12 rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 pl-11 pr-4 text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="email-input"
            />
          </div>
        </div>

        {/* Password field */}
        <div>
          <label className="block text-xs font-bold mb-1.5 text-zinc-900 dark:text-white">{c.password}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={c.passwordPlaceholder}
              className="w-full h-12 rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 pl-11 pr-12 text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-3 hover:bg-[#333] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {c.loading}
            </>
          ) : (
            isLogin ? c.loginBtn : c.registerBtn
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-600"></div>
        <span className="text-xs text-zinc-400 dark:text-zinc-500">{c.orContinueWith}</span>
        <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-600"></div>
      </div>

      {/* Google Login Button */}
      <button
        onClick={onSwitchToGoogle}
        className="w-full rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-3 hover:bg-zinc-50 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all flex items-center justify-center gap-2"
        data-testid="google-btn"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
        {c.googleLogin}
      </button>
    </div>
  );
}
